/**
 * T12View — Trailing 12 Months report table.
 *
 * Wide table: [Category | Jan | Feb | ... | Dec | Total]. Sections stack:
 *   Income → COGS → Gross Profit → Operating Expenses → Net Income.
 */
import { fmtMoney } from "@/lib/format";
import { FileText, FileSpreadsheet, Mail } from "lucide-react";

function monthLabel(mkey) {
  // "2026-02" -> "Feb 26"
  const [y, m] = mkey.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
}

function Cell({ value, muted, bold }) {
  const isZero = value === 0 || value == null;
  return (
    <td
      className={`px-2 py-1.5 text-right font-mono-fin text-xs whitespace-nowrap ${bold ? "font-bold" : ""} ${isZero ? "text-[var(--ll-ink-muted)]" : ""}`}
      style={{ color: muted && !isZero ? "var(--ll-ink-soft)" : undefined }}
    >
      {isZero ? "—" : fmtMoney(value)}
    </td>
  );
}

export default function T12View({ data, onExport, onShare }) {
  if (!data) return null;
  const months = data.months || [];

  const renderSection = (title, bucket, sectionKey) => (
    <>
      <tr className="bg-[#0b5d9e]" data-testid={`t12-section-${sectionKey}`}>
        <td colSpan={months.length + 2} className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-white font-medium">
          {title}
        </td>
      </tr>
      {bucket.rows.length === 0 && (
        <tr>
          <td className="px-3 py-2 text-xs italic text-[var(--ll-ink-muted)]" colSpan={months.length + 2}>
            No {title.toLowerCase()} activity in this period.
          </td>
        </tr>
      )}
      {bucket.rows.map((r) => (
        <tr key={`${sectionKey}-${r.category}`} className="border-b hover:bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`t12-row-${sectionKey}-${r.category.replace(/\s+/g, "-").toLowerCase()}`}>
          <td className="px-3 py-1.5 text-xs whitespace-nowrap">{r.category}</td>
          {r.monthly.map((v, i) => (
            <Cell key={i} value={v} muted />
          ))}
          <Cell value={r.total} bold />
        </tr>
      ))}
      <tr className="border-b bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
        <td className="px-3 py-1.5 text-xs font-bold whitespace-nowrap">Total {title}</td>
        {bucket.totals.map((v, i) => (
          <Cell key={i} value={v} bold />
        ))}
        <Cell value={bucket.grand_total} bold />
      </tr>
    </>
  );

  return (
    <div className="ll-card-flat overflow-hidden" data-testid="t12-view">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">Trailing 12 Months</div>
          <div className="font-display font-bold text-xl mt-0.5">T-12 Report</div>
          <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5 font-mono-fin">
            {months.length > 0 ? `${monthLabel(months[0])} → ${monthLabel(months[months.length - 1])}` : "—"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onExport("t12", "pdf")}
            data-testid="t12-export-pdf"
            className="text-xs px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <FileText size={13} /> PDF
          </button>
          <button
            onClick={() => onExport("t12", "xlsx")}
            data-testid="t12-export-xlsx"
            className="text-xs px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <FileSpreadsheet size={13} /> Excel
          </button>
          {onShare && (
            <button
              onClick={() => onShare("t12")}
              data-testid="t12-share-email"
              title="Share by email"
              className="text-xs px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 flex items-center gap-1.5"
            >
              <Mail size={13} /> Share by email
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="t12-table">
          <thead>
            <tr className="border-b bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-border)" }}>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium sticky left-0 bg-[var(--ll-bg-sidebar)] z-[1] min-w-[220px]">Category</th>
              {months.map((m) => (
                <th key={m} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest font-medium whitespace-nowrap">{monthLabel(m)}</th>
              ))}
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest font-medium whitespace-nowrap bg-[var(--ll-mint)]">Total</th>
            </tr>
          </thead>
          <tbody>
            {renderSection("Income", data.income, "income")}
            {renderSection("COGS", data.cogs, "cogs")}

            {/* Gross Profit */}
            <tr className="border-b bg-[#F1F5F0]" style={{ borderColor: "var(--ll-border)" }} data-testid="t12-gross-profit">
              <td className="px-3 py-2 text-xs font-bold whitespace-nowrap">Gross Profit</td>
              {data.gross_profit.monthly.map((v, i) => (
                <Cell key={i} value={v} bold />
              ))}
              <Cell value={data.gross_profit.total} bold />
            </tr>

            {renderSection("Operating Expenses", data.expense, "expense")}

            {/* Net Income */}
            <tr className="text-white" style={{ background: "var(--ll-ink)" }} data-testid="t12-net-income">
              <td className="px-3 py-2.5 text-sm font-bold whitespace-nowrap">Net Income</td>
              {data.net_income.monthly.map((v, i) => {
                const isZero = v === 0 || v == null;
                return (
                  <td key={i} className="px-2 py-2.5 text-right font-mono-fin text-xs font-bold whitespace-nowrap">
                    {isZero ? "—" : fmtMoney(v)}
                  </td>
                );
              })}
              <td className="px-2 py-2.5 text-right font-mono-fin text-sm font-bold whitespace-nowrap bg-black/15">
                {fmtMoney(data.net_income.total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
